export type EventStatus = {
  label: string;
  sublabel?: string;
};

const twoDigits = (num: number): string => num.toString().padStart(2, '0');

// Server time is UTC
export const getServerNow = (): Date => new Date();

// Arms Race: daily 4-hour windows with rotating themes
const armsRaceSchedule = [
  ['Hero Advancement', 'Base Building', 'Unit Progression', 'Tech Research', 'Drone Boost', 'Hero Advancement'],
  ['Base Building', 'Unit Progression', 'Tech Research', 'Drone Boost', 'Hero Advancement', 'Base Building'],
  ['Unit Progression', 'Tech Research', 'Drone Boost', 'Hero Advancement', 'Base Building', 'Unit Progression'],
  ['Tech Research', 'Drone Boost', 'Hero Advancement', 'Base Building', 'Unit Progression', 'Tech Research'],
  ['Drone Boost', 'Hero Advancement', 'Base Building', 'Unit Progression', 'Tech Research', 'Drone Boost'],
  ['Hero Advancement', 'Base Building', 'Unit Progression', 'Tech Research', 'Drone Boost', 'Hero Advancement'],
  ['Base Building', 'Unit Progression', 'Tech Research', 'Drone Boost', 'Hero Advancement', 'Base Building'],
];
const armsRaceTimes = ['00:00:00', '04:00:00', '08:00:00', '12:00:00', '16:00:00', '20:00:00'];

export function getArmsRaceNext(): EventStatus {
  const now = getServerNow();
  const dayIndex = (now.getUTCDay() + 6) % 7; // Monday = 0

  for (let dayOffset = 0; dayOffset < 8; dayOffset += 1) {
    const candidateDayIndex = (dayIndex + dayOffset) % armsRaceSchedule.length;
    const candidateDate = new Date(now);
    candidateDate.setUTCDate(now.getUTCDate() + dayOffset);

    for (let i = 0; i < armsRaceTimes.length; i += 1) {
      const [h, m, s] = armsRaceTimes[i].split(':').map(Number);
      const slotDate = new Date(candidateDate);
      slotDate.setUTCHours(h, m, s, 0);
      if (slotDate > now) {
        const theme = armsRaceSchedule[candidateDayIndex][i];
        const countdown = formatCountdown(slotDate, now);
        return {
          label: `Next: ${theme} @ ${armsRaceTimes[i]} UTC`,
          sublabel: `Starts in ${countdown}`,
        };
      }
    }
  }

  return { label: 'Schedule unavailable' };
}

// Wanted Boss: Monday (1) & Thursday (4) at 00:00, 06:00, 12:00, 18:00, 3h duration
const wantedBossDays = [1, 4]; // Monday, Thursday
const wantedBossTimes = ['00:00', '06:00', '12:00', '18:00'];

export function getWantedBossNext(): EventStatus {
  const now = getServerNow();
  const today = now.getUTCDay(); // 0=Sun

  for (let dayOffset = 0; dayOffset <= 7; dayOffset += 1) {
    const candidate = new Date(now);
    candidate.setUTCDate(now.getUTCDate() + dayOffset);
    const candidateDay = (today + dayOffset) % 7;
    const normalizedDay = candidateDay === 0 ? 7 : candidateDay; // treat Sunday as 7

    if (!wantedBossDays.includes(normalizedDay)) continue;

    for (const time of wantedBossTimes) {
      const [h, m] = time.split(':').map(Number);
      const slotDate = new Date(candidate);
      slotDate.setUTCHours(h, m, 0, 0);
      if (slotDate > now) {
        const countdown = formatCountdown(slotDate, now);
        return {
          label: `${slotDate.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })} @ ${time} UTC`,
          sublabel: `Starts in ${countdown}`,
        };
      }
    }
  }

  const next = new Date(now);
  const daysUntilMonday = (8 - today) % 7 || 7;
  next.setUTCDate(now.getUTCDate() + daysUntilMonday);
  next.setUTCHours(0, 0, 0, 0);
  return {
    label: `${next.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })} @ 00:00 UTC`,
    sublabel: 'Starts in 7+ days',
  };
}

// Alliance Duel: weekly cycle; we’ll surface as ongoing
export function getAllianceDuelStatus(): EventStatus {
  return {
    label: 'Weekly duel',
    sublabel: 'Check daily theme and contribute points',
  };
}

const formatCountdown = (target: Date, now: Date): string => {
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return 'Now';
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${twoDigits(hours)}:${twoDigits(minutes)}:${twoDigits(seconds)}`;
};

